import React, { useContext, useEffect, useState } from "react";
import {
  Text,
  Box,
  Button,
  Column,
  Columns,
  Dialog,
  Stack,
} from "@origin-digital/ods-core";
import { Autocomplete } from "@origin-digital/ods-lab";
import { DatePicker, DatePickerProvider } from "@origin-digital/ods-pickers";
import {
  OPERATIONAL_TEAM_FILTER_OPTIONS,
  REQUEST_TYPE_FILTER_OPTIONS,
  RequestFilter,
  RequestStatusFilterOption,
  State,
} from "./types";
import { apiClient } from "../../api/apiService";
import { Companies, Company } from "../Projects/types";
import { useDebounce } from "react-use";
import { AppContext } from "src/context/AppContext";

interface IFilterPremisesDialog {
  open: boolean;
  setOpen: (open: boolean) => void;
  appliedFilters: RequestFilter;
  setAppliedFilters: (appliedFilters: RequestFilter) => void;
}

export const FilterPremisesDialog = (props: IFilterPremisesDialog) => {
  const { open, setOpen, appliedFilters, setAppliedFilters } = props;
  const [loading, setLoading] = useState<boolean>(true);
  const [filtersToApply, setFiltersToApply] = useState<RequestFilter>({});
  const [companies, setCompanies] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const [isCreatedFromValid, setIsCreatedFromValid] = useState<boolean>(true);
  const [createdFromHelpText, setCreatedFromHelpText] =
    useState<string>("dd/mm/yyyy");
  const [isCreatedToValid, setIsCreatedToValid] = useState<boolean>(true);
  const [createdToHelpText, setCreatedToHelpText] =
    useState<string>("dd/mm/yyyy");

  const {
    context: { premiseDashboardFilters: filtersFromContext },
  } = useContext(AppContext);
  useEffect(() => {
    if (filtersFromContext) setFiltersToApply(filtersFromContext);
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get<Companies>("/company/companies");
        setCompanies(
          response.data.companies.map((company: Company) => ({
            label: company.name,
            value: company.id,
          }))
        );
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch companies:", err);
        setLoading(false);
      }
    };
    fetchCompanies();
  }, [filtersFromContext]);

  // inline validate createdFrom
  useDebounce(
    () => {
      if (!filtersToApply?.createdFrom) {
        setIsCreatedFromValid(true);
        setCreatedFromHelpText("dd/mm/yyyy");
        return;
      }
      if (filtersToApply?.createdFrom?.toString() === "Invalid Date") {
        setIsCreatedFromValid(false);
        setCreatedFromHelpText("Oops, date must be in dd/mm/yyyy format.");
        return;
      }
      if (
        filtersToApply?.createdFrom?.getFullYear() &&
        filtersToApply?.createdFrom?.getFullYear() < 1000
      ) {
        setIsCreatedFromValid(false);
        setCreatedFromHelpText("Oops, date must be in dd/mm/yyyy format.");
        return;
      }
      if (filtersToApply?.createdFrom > new Date()) {
        setIsCreatedFromValid(false);
        setCreatedFromHelpText("Oops, date cannot be in the future.");
        return;
      }
      setIsCreatedFromValid(true);
      setCreatedFromHelpText("dd/mm/yyyy");
    },
    500,
    [filtersToApply?.createdFrom]
  );

  // inline validate createdTo
  useDebounce(
    () => {
      if (!filtersToApply?.createdTo) {
        setIsCreatedToValid(true);
        setCreatedToHelpText("dd/mm/yyyy");
        return;
      }
      if (filtersToApply.createdTo.toString() === "Invalid Date") {
        setIsCreatedToValid(false);
        setCreatedToHelpText("Oops, date must be in dd/mm/yyyy format.");
        return;
      }
      if (filtersToApply.createdTo > new Date()) {
        setIsCreatedToValid(false);
        setCreatedToHelpText("Oops, date cannot be in the future.");
        return;
      }
      if (
        filtersToApply.createdFrom &&
        filtersToApply.createdTo < filtersToApply.createdFrom
      ) {
        setIsCreatedToValid(false);
        setCreatedToHelpText(
          "Oops, the 'To' date can't be before the 'From' date."
        );
        return;
      }
      setIsCreatedToValid(true);
      setCreatedToHelpText("dd/mm/yyyy");
    },
    500,
    [filtersToApply?.createdTo, filtersToApply?.createdFrom]
  );

  const isValidToApply = (): boolean => {
    if (
      filtersToApply?.createdTo instanceof Date &&
      !filtersToApply.createdFrom
    ) {
      setIsCreatedFromValid(false);
      setCreatedFromHelpText("Oops, you must enter a 'From' date.");
      return false;
    }
    return isCreatedFromValid && isCreatedToValid;
  };

  const handleApply = () => {
    setAppliedFilters(filtersToApply);
    setOpen(false);
  };

  const handleReset = () => {
    setFiltersToApply({});
  };

  const handleCancel = () => {
    setFiltersToApply(appliedFilters ?? {});
    setOpen(false);
  };

  return (
    <Dialog
      id="filter-premises-dialog"
      open={open}
      title="Filter premises"
      maxWidth="sm"
      stickyTitle={true}
      headerMargin="xlarge"
      onClose={handleCancel}
    >
      <Stack space="xsmall">
        {/* State */}
        <Autocomplete<string[]>
          id="state"
          label="State"
          //@ts-expect-error: it works but throws error in ts
          options={Object.values(State)}
          textFieldProps={{
            variant: "outlined",
          }}
          muiProps={{
            //@ts-expect-error: it works but throws error in ts
            multiple: "true",
            limitTags: 4,
            //@ts-expect-error: it works but throws error in ts
            disableCloseOnSelect: "true",
          }}
          sx={{
            ".MuiInputBase-root.MuiOutlinedInput-root.MuiAutocomplete-inputRoot":
              {
                display: "flex",
                flexWrap: "wrap", // Ensures wrapped items
              },
          }}
          value={filtersToApply?.state ?? []}
          onChange={(_, selected?: string[] | null) => {
            setFiltersToApply({
              ...filtersToApply,
              state: selected && selected.length > 0 ? selected : undefined,
            });
          }}
        />
        {/* End State */}
        {/* Service */}
        <Autocomplete<string[]>
          id="service"
          label="Service"
          //@ts-expect-error: it works but throws error in ts
          options={["Electricity", "Natural gas", "Internet"]}
          textFieldProps={{
            variant: "outlined",
          }}
          muiProps={{
            //@ts-expect-error: it works but throws error in ts
            multiple: "true",
            //@ts-expect-error: it works but throws error in ts
            disableCloseOnSelect: "true",
          }}
          sx={{
            ".MuiInputBase-root.MuiOutlinedInput-root.MuiAutocomplete-inputRoot":
              {
                display: "flex",
                flexWrap: "wrap", // Ensures wrapped items
              },
          }}
          value={filtersToApply?.serviceType ?? []}
          onChange={(_, selected?: string[] | null) => {
            setFiltersToApply({
              ...filtersToApply,
              serviceType:
                selected && selected?.length > 0 ? selected : undefined,
            });
          }}
        />
        {/* End Service */}
        {/* Request type */}
        <Autocomplete<string[]>
          id="request-type"
          label="Request type"
          //@ts-expect-error: it works but throws error in ts
          options={REQUEST_TYPE_FILTER_OPTIONS}
          textFieldProps={{
            variant: "outlined",
          }}
          muiProps={{
            //@ts-expect-error: it works but throws error in ts
            multiple: "true",
            limitTags: 2,
            //@ts-expect-error: it works but throws error in ts
            disableCloseOnSelect: "true",
          }}
          sx={{
            ".MuiInputBase-root.MuiOutlinedInput-root.MuiAutocomplete-inputRoot":
              {
                display: "flex",
                flexWrap: "wrap", // Ensures wrapped items
              },
          }}
          value={filtersToApply?.requestType ?? []}
          onChange={(_, selected?: string[] | null) => {
            setFiltersToApply({
              ...filtersToApply,
              requestType:
                selected && selected?.length > 0 ? selected : undefined,
            });
          }}
        />
        {/* End Request type */}
        {/* Status */}
        <Autocomplete<string[]>
          id="status"
          label="Status"
          //@ts-expect-error: it works but throws error in ts
          options={Object.values(RequestStatusFilterOption)}
          textFieldProps={{
            variant: "outlined",
          }}
          muiProps={{
            //@ts-expect-error: it works but throws error in ts
            multiple: "true",
            limitTags: 2,
            //@ts-expect-error: it works but throws error in ts
            disableCloseOnSelect: "true",
          }}
          sx={{
            ".MuiInputBase-root.MuiOutlinedInput-root.MuiAutocomplete-inputRoot":
              {
                display: "flex",
                flexWrap: "wrap", // Ensures wrapped items
              },
          }}
          value={filtersToApply?.status ?? []}
          onChange={(_, selected?: string[] | null) => {
            setFiltersToApply({
              ...filtersToApply,
              status: selected && selected?.length > 0 ? selected : undefined,
            });
          }}
        />
        {/* End Status */}
        {/* Requesting company */}
        <Autocomplete
          disabled={loading}
          textFieldProps={{
            variant: "outlined",
          }}
          id="company"
          label="Requesting company"
          options={companies}
          value={
            companies.find(
              (c) => c.value === filtersToApply?.requestingCompanyId
            ) ?? null
          }
          onChange={(_e, item) => {
            setFiltersToApply({
              ...filtersToApply,
              requestingCompanyId: item?.value ? item.value : undefined,
            });
          }}
        />
        {/* End Requesting company */}

        {/* Ops Team */}
        <Autocomplete<string[]>
          id="ops-team"
          label="Operational team"
          //@ts-expect-error: it works but throws error in ts
          options={OPERATIONAL_TEAM_FILTER_OPTIONS}
          textFieldProps={{
            variant: "outlined",
          }}
          muiProps={{
            //@ts-expect-error: it works but throws error in ts
            multiple: "true",
            limitTags: 3,
            //@ts-expect-error: it works but throws error in ts
            disableCloseOnSelect: "true",
          }}
          sx={{
            ".MuiInputBase-root.MuiOutlinedInput-root.MuiAutocomplete-inputRoot":
              {
                display: "flex",
                flexWrap: "wrap", // Ensures wrapped items
              },
          }}
          value={filtersToApply?.operationalTeam ?? []}
          onChange={(_, selected?: string[] | null) => {
            setFiltersToApply({
              ...filtersToApply,
              operationalTeam:
                selected && selected?.length > 0 ? selected : undefined,
            });
          }}
        />
        {/* End Ops Team */}

        {/* Created date */}
        <Stack space="medium">
          <Text weight="medium">Requests created</Text>
          <Columns collapseBelow="md" space={["large", "medium"]}>
            <Column>
              <DatePickerProvider>
                <DatePicker
                  id="datepicker"
                  label="From"
                  muiProps={{
                    slotProps: {
                      textField: {
                        variant: "outlined",
                      },
                    },
                  }}
                  disableFuture
                  value={filtersToApply?.createdFrom ?? null}
                  onChange={(date) => {
                    if (date != filtersToApply?.createdFrom) {
                      setFiltersToApply({
                        ...filtersToApply,
                        createdFrom: date ?? undefined,
                      });
                    }
                  }}
                  helperText={createdFromHelpText}
                  error={!isCreatedFromValid}
                />
              </DatePickerProvider>
            </Column>
            <Column>
              <DatePickerProvider>
                <DatePicker
                  id="datepicker"
                  label="To"
                  helperText={createdToHelpText}
                  muiProps={{
                    slotProps: {
                      textField: {
                        variant: "outlined",
                      },
                    },
                  }}
                  minDate={filtersToApply?.createdFrom ?? null}
                  disableFuture
                  error={!isCreatedToValid}
                  value={filtersToApply?.createdTo ?? null}
                  onChange={(date) => {
                    if (date != filtersToApply?.createdTo) {
                      setFiltersToApply({
                        ...filtersToApply,
                        createdTo: date ?? undefined,
                      });
                    }
                  }}
                />
              </DatePickerProvider>
            </Column>
          </Columns>
        </Stack>
        {/* End Created date */}

        {/* Actions */}
        <Box
          position={{
            lg: "static",
            xs: "fixed",
          }}
          paddingTop={{
            lg: "large",
            xs: "medium",
          }}
          paddingBottom={{
            lg: "none",
            xs: "large",
          }}
          paddingX={{
            lg: "none",
            xs: "medium",
          }}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          backgroundColor="white"
          width="full"
          style={{
            left: "0",
            bottom: "0",
            zIndex: "1",
          }}
        >
          <Button
            variant="text"
            color="primary"
            noTextPadding
            onClick={handleReset}
          >
            Reset
          </Button>
          <Box
            display="flex"
            flex-wrap={{
              lg: "noWrap",
              xs: "wrap",
            }}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Box marginRight="large">
              <Button variant="text" color="primary" onClick={handleCancel}>
                Cancel
              </Button>
            </Box>
            <Button
              disabled={loading}
              onClick={() => {
                if (isValidToApply()) {
                  handleApply();
                }
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
        {/* End Actions */}
      </Stack>
    </Dialog>
  );
};
